import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import { createArtist, editArtist, getArtistByID } from '../../store/actions/artist/artist.action';
import { getAwsKey } from '../../store/actions/piece-video/piece-video.action';
import { getInstruments } from '../../store/actions/instrument/instrument.action';
import { createMember, deleteMember, editMember } from '../../store/actions/member/member.action';
import CreateArtist from '../../components/Artist/CreateArtist';
import CreateMember from '../../components/Artist/CreateMember';
import PageLoader from '../../components/Common/PageLoader';

import '../../styles/artist.css';

const CreateArtistContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const isCreating = useSelector((state) => state.artist.isCreating);
  const instruments = useSelector((state) => state.instrument.list);
  const artists = useSelector((state) => state.artist.list);
  const currentArtist = useSelector((state) => state.artist.currentArtist);
  const categories = useSelector((state) => state.auth.categories);

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [startRender, setStartRender] = useState(false);
  const [artistToBeEdit, setArtistToBeEdit] = useState(null);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      if (!categories || !Array.isArray(categories)) {
        setAccessDenied(true);
        return;
      }

      const artistCategory = categories.find(category => category.link === 'artists');

      if (artistCategory && artistCategory.mode) {
        setAccessMode(artistCategory.mode);
        setAccessDenied(false);
      } else {
        setAccessDenied(true);
      }
    } catch (err) {
      console.error('Error setting access:', err);
      setAccessDenied(true);
    }
  }, [categories]);

  // Get artist by ID
  const fetchArtistByID = useCallback(async (artistId) => {
    try {
      const artist = await dispatch(getArtistByID(artistId));
      setArtistToBeEdit(artist);
      setStartRender(true);
    } catch (error) {
      console.error('Error getting artist by ID:', error);
      navigate('/artists');
    }
  }, [dispatch, navigate]);

  // Process create or edit mode
  const processCreateOREdit = useCallback(async (editMode, artistId) => {
    try {
      await dispatch(getInstruments());

      if (editMode === 'EDIT' && artistId) {
        await fetchArtistByID(artistId);
      } else {
        setStartRender(true);
      }
    } catch (error) {
      console.error('Error in processCreateOREdit:', error);
      navigate('/artists');
    }
  }, [dispatch, fetchArtistByID, navigate]);

  // Action handlers
  const handleCreateArtist = useCallback((artistData) => {
    return dispatch(createArtist(artistData));
  }, [dispatch]);

  const handleEditArtist = useCallback((artistData) => {
    return dispatch(editArtist(artistData));
  }, [dispatch]);

  const handleGetAwsKey = useCallback((data) => {
    return dispatch(getAwsKey(data));
  }, [dispatch]);

  const handleGetArtistByID = useCallback((artistId) => {
    return dispatch(getArtistByID(artistId));
  }, [dispatch]);

  const handleCreateMember = useCallback((memberData) => {
    return dispatch(createMember(memberData));
  }, [dispatch]);

  const handleEditMember = useCallback((memberData) => {
    return dispatch(editMember(memberData));
  }, [dispatch]);

  const handleDeleteMember = useCallback((member) => {
    return dispatch(deleteMember(member));
  }, [dispatch]);

  // Initialize on mount
  useEffect(() => {
    const editMode = params && params.id ? 'EDIT' : 'CREATE';
    const artistId = params ? params.id : null;

    setMode(editMode);
    processCreateOREdit(editMode, artistId);
    setAccess();
  }, [params, processCreateOREdit, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state
  if (!startRender) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  // Prepare info objects
  const artistInfo = {
    isCreating,
    createArtist: handleCreateArtist,
    getAwsKey: handleGetAwsKey,
    editArtist: handleEditArtist,
    initialValues: artistToBeEdit,
    mode: mode,
  };

  const memberInfo = {
    instruments,
    getAwsKey: handleGetAwsKey,
    createMember: handleCreateMember,
    deleteMember: handleDeleteMember,
    editMember: handleEditMember,
    artists,
    currentArtist,
    getArtistByID: handleGetArtistByID,
  };

  return (
    <div className="row artist-create-container">
      <div className="col-md-6">
        <CreateArtist accessMode={accessMode} artistInfo={artistInfo} />
      </div>
      {currentArtist ? (
        <div className="col-md-6">
          <CreateMember accessMode={accessMode} memberInfo={memberInfo} />
        </div>
      ) : null}
    </div>
  );
};

export default CreateArtistContainer;
